import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { BookOpenText, Crown, Lightning, ShieldCheck, ChatTeardropDots } from "@phosphor-icons/react";

export default function Playbook() {
  const [playbook, setPlaybook] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/playbook")
      .then((r) => {
        setPlaybook(r.data.playbook);
        if (r.data.playbook?.length) setActive(r.data.playbook[0]);
      })
      .catch(() => toast.error("Failed to load playbook"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="playbook-page">
        <div className="mb-8">
          <p className="eyebrow">
            <span className="gold-divider" /> Advisor · Master Sales Playbook
          </p>
          <h1 className="font-display text-4xl mt-3">Conversion-Ready Playbooks</h1>
          <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
            Word-for-word objection responses, deep-dive talking points, and the exact closes that
            convert prospects into clients. Built for the booking & reserved-call flow.
          </p>
        </div>

        {loading ? (
          <p className="text-exodus-muted">Loading playbook…</p>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* LEFT — product nav */}
            <nav className="lg:col-span-3 space-y-2 lg:sticky lg:top-6 self-start">
              {playbook.map((p) => (
                <button
                  key={p.product_key}
                  data-testid={`playbook-tab-${p.product_key}`}
                  onClick={() => setActive(p)}
                  className={`w-full text-left px-4 py-3 border transition-all ${
                    active?.product_key === p.product_key
                      ? "border-exodus-gold bg-exodus-elevated"
                      : "border-exodus-border hover:border-exodus-gold/40"
                  }`}
                >
                  <p className="eyebrow text-[10px]">{p.product_key.replace("_", " ")}</p>
                  <p className="font-display text-base mt-1">{p.product_label}</p>
                </button>
              ))}
            </nav>

            {/* RIGHT — content */}
            {active && (
              <div className="lg:col-span-9 space-y-6">
                {/* Headline */}
                <div className="card-surface p-6">
                  <p className="eyebrow text-xs">Headline framing</p>
                  <h2 className="font-display text-2xl lg:text-3xl mt-2 text-exodus-gold">
                    {active.headline}
                  </h2>
                </div>

                {/* Elevator pitch */}
                <Section icon={Lightning} title="60-Second Elevator Pitch">
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {active.elevator_pitch}
                  </p>
                </Section>

                {/* Deep dive */}
                <Section icon={BookOpenText} title="Deep-Dive Talking Points">
                  <ul className="space-y-3">
                    {active.deep_dive.map((d, i) => (
                      <li
                        key={i}
                        className="text-sm leading-relaxed border-l-2 border-exodus-gold/60 pl-4"
                      >
                        {d}
                      </li>
                    ))}
                  </ul>
                </Section>

                {/* Objections */}
                <Section icon={ChatTeardropDots} title={`Top ${active.objections.length} Objections — Word-for-Word Responses`}>
                  <div className="space-y-4">
                    {active.objections.map((o, i) => (
                      <details
                        key={i}
                        data-testid={`objection-${active.product_key}-${i}`}
                        className="border border-exodus-border bg-exodus-elevated/50 px-4 py-3 cursor-pointer group"
                      >
                        <summary className="cursor-pointer flex items-start gap-3 list-none">
                          <span className="text-exodus-gold font-mono text-xs mt-1">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-display text-base flex-1">"{o.objection}"</span>
                          <span className="text-exodus-muted text-xs group-open:hidden">show</span>
                          <span className="text-exodus-muted text-xs hidden group-open:inline">hide</span>
                        </summary>
                        <p className="text-sm leading-relaxed mt-4 pl-8 text-exodus-text/90 whitespace-pre-line">
                          {o.response}
                        </p>
                      </details>
                    ))}
                  </div>
                </Section>

                {/* Why they don't buy */}
                <Section icon={ShieldCheck} title="Why Prospects DON'T Buy — and How to Disarm Each">
                  <div className="grid md:grid-cols-2 gap-4">
                    {active.why_they_dont_buy.map(([reason, disarm], i) => (
                      <div key={i} className="border border-exodus-border p-4 bg-exodus-elevated/40">
                        <p className="eyebrow text-[10px] mb-2">Reason {String(i + 1).padStart(2, "0")}</p>
                        <p className="font-display text-sm">{reason}</p>
                        <p className="text-xs text-exodus-muted mt-2 leading-relaxed">{disarm}</p>
                      </div>
                    ))}
                  </div>
                </Section>

                {/* Close */}
                <Section icon={Crown} title="The Conversion Close — Use Verbatim">
                  <div className="bg-exodus-gold/10 border border-exodus-gold p-5">
                    <p className="text-sm leading-relaxed italic">{active.conversion_close}</p>
                  </div>
                </Section>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="card-surface p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-exodus-gold" weight="thin" />
        <h3 className="eyebrow text-xs">{title}</h3>
      </div>
      {children}
    </div>
  );
}
